/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

// AuthConfig needed for OAuthAuthenticationService
export const authenticationConfig: AuthConfig = {
  // URL of the SPA to redirect the user to after login
  redirectUri: window.location.origin + '/login',

  // The SPA's id. The SPA is registerd with this id at the auth-server
  clientId: 'ims.app',

  // set the scope for the permissions the client should request
  // The first three are defined by OIDC.
  scope: 'openid profile email offline_access',

  // If true, the lib will try to check whether the user
  // is still logged in on a regular basis
  sessionChecksEnabled: true,

  oidc: false,
};

import { LoggingService } from '../../logging/logic/logging.service';
import { AuthenticationService } from '../authentication.service';
import { PasswordCredentials } from 'src/app/_model/credentials';
import { MobileOAuthStorageService } from '../../mobile-oauth-storage/mobile-oauth-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../alert/alert.service';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * An [[AuthenticationService]] implementation authenticating with an OAuth Server
 */
@Injectable({
  providedIn: 'root',
})
export class OauthAuthenticationService implements AuthenticationService {
  // Public Members
  public authenticationState = new BehaviorSubject(false);
  public setup = new BehaviorSubject({ finished: false, error: undefined });

  // Private Member
  private setupFinished: Promise<any>;
  private identityServer: string;

  // Lifecycle
  constructor(
    private oauthService: OAuthService,
    private logger: LoggingService,
    private mobileOauthStorage: MobileOAuthStorageService,
    private translate: TranslateService,
    private alertService: AlertService,
  ) {}

  public finishedSetup(): Promise<any> {
    return this.setupFinished;
  }

  // AuthenticationService
  public async setIdentityServer(identityServer: string) {
    console.log(
      this.constructor.name,
      'setIdentityServer called with',
      identityServer,
    );
    if (identityServer === undefined) {
      this.setup.next({
        finished: false,
        error: new Error('Undefined IdentityServer'),
      });
      this.setupFinished = Promise.reject();
      return;
    }

    if (identityServer === this.identityServer) {
      this.logger.log(
        this.constructor.name,
        '- received identical identityServer abort setting up oauth twice',
        this.identityServer,
        identityServer,
      );
      return;
    }
    this.identityServer = identityServer;

    let config = authenticationConfig;
    config.issuer = identityServer;
    config.disableAtHashCheck = true;

    this.logger.log(
      this.constructor.name,
      '- Setting up OAuth for',
      identityServer,
      config,
    );

    this.setupFinished = new Promise((resolve, reject) => {
      this.setupOAuth(config)
        .then(() => {
          this.logger.log(
            this.constructor.name,
            '- Finished setting up OAuth for',
            identityServer,
          );
          this.setup.next({
            finished: true,
            error: undefined,
          });

          resolve(true);
        })
        .catch((error) => {
          this.logger.error(
            this.constructor.name,
            '- Error setting up OAuth',
            error?.message,
          );
          this.setup.next({
            finished: false,
            error: error,
          });

          reject();
        });
    });
  }

  public async login(credentials: PasswordCredentials) {
    if (!this.setup.getValue().finished) {
      if (this.setup.getValue().error) {
        this.logger.error(
          this.constructor.name,
          '- Could not login because the setup of OAuth failed',
          this.setup.getValue().error?.message,
        );
        throw this.setup.getValue().error;
      } else {
        this.logger.error(
          this.constructor.name,
          '- Could not login because the setup of OAuth is not finished',
        );
      }
    }

    try {
      await this.oauthService.loadDiscoveryDocument();
    } catch (reason) {
      this.logger.warn(
        this.constructor.name,
        'could not connect to keycloak, continue in offline mode (1)',
      );
    }

    try {
      let resp = await this.oauthService.fetchTokenUsingPasswordFlow(
        credentials.username,
        credentials.password,
      );
      this.logger.log(
        this.constructor.name,
        '- User logged in with response',
        resp,
      );
      let resp2 = await this.oauthService.loadUserProfile();
      this.logger.log(
        this.constructor.name,
        '- loadUserProfile with response',
        resp2,
      );
      this.setNextAuthenticationState(true);
      return resp;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Could not log in user: ',
        error?.message,
      );
      throw error;
    }
  }

  public async logout() {
    if (!this.setup.getValue().finished) {
      if (this.setup.getValue().error) {
        this.logger.error(
          this.constructor.name,
          '- Could not logout because the setup of OAuth failed',
          this.setup.getValue().error?.message,
        );
        throw this.setup.getValue().error;
      } else {
        this.logger.error(
          this.constructor.name,
          '- Could not logout because the setup of OAuth is not finished',
        );
      }
    }

    let title = await this.translate.get('COMMON.LOGOUT.TITLE').toPromise();
    let subtitle = await this.translate
      .get('COMMON.LOGOUT.SUBTITLE')
      .toPromise();
    let message = await this.translate.get('COMMON.LOGOUT.MESSAGE').toPromise();
    let cancel = await this.translate.get('COMMON.LOGOUT.CANCEL').toPromise();
    let confirm = await this.translate.get('COMMON.LOGOUT.CONFIRM').toPromise();

    await this.alertService.showAlert(
      title,
      subtitle,
      message,
      confirm,
      this.logoutWithoutAlert.bind(this),
      undefined,
      undefined,
      true,
      'danger',
    );
  }

  public async isAuthenticated() {
    if (!this.setup.getValue().finished) {
      if (this.setup.getValue().error) {
        this.logger.error(
          this.constructor.name,
          '- Could not check authentication state because the setup of OAuth failed',
          this.setup.getValue().error?.message,
        );
        throw this.setup.getValue().error;
      } else {
        this.logger.error(
          this.constructor.name,
          '- Could not check authentication state because the setup of OAuth is not finished',
        );
      }
    }

    return this.authenticationState.value;
  }

  public async authenticate(): Promise<any> {
    if (!this.setup.getValue().finished) {
      if (this.setup.getValue().error) {
        this.logger.error(
          this.constructor.name,
          '- Could not authenticate state because the setup of OAuth failed',
          this.setup.getValue().error?.message,
        );
        throw this.setup.getValue().error;
      } else {
        this.logger.error(
          this.constructor.name,
          '- Could not authenticate because the setup of OAuth is not finished',
        );
      }
    }

    this.logger.log(this.constructor.name, '- Trying to authenticate the user');

    if (this.oauthService.hasValidAccessToken()) {
      this.logger.log(this.constructor.name, '- Valid Access Token available');
      this.setNextAuthenticationState(true);
      return true;
    } else {
      if (this.oauthService.getRefreshToken()) {
        try {
          this.logger.log(this.constructor.name, '- Try refreshing token');
          await this.refreshToken();
          this.logger.log(this.constructor.name, '- Token refreshed');
          this.setNextAuthenticationState(true);
        } catch (error) {
          this.logger.error(
            this.constructor.name,
            '- Could not refresh token',
            error?.message,
          );
          this.setNextAuthenticationState(false);
        }
      } else {
        this.logger.log(this.constructor.name, '- No Refresh Token available');
      }
    }
  }

  public getAuthorizationHeader(): string {
    return 'Bearer ' + this.oauthService.getAccessToken();
  }

  public getUserName(): string {
    const claims = this.oauthService.getIdentityClaims();
    return claims ? claims['preferred_username'] : '';
  }

  public logoutWithoutAlert() {
    this.logger.log(this.constructor.name, '- Log out user');
    this.oauthService.logOut(true);
    this.setNextAuthenticationState(false);
  }

  public async refreshToken(): Promise<boolean> {
    this.logger.log(
      this.constructor.name,
      'waiting for setup to finish before refreshing token',
    );
    let result = await this.setupFinished;
    this.logger.log(
      this.constructor.name,
      'setup finish with',
      result,
      'continue refreshing',
      'tokenEndpoint',
      this.oauthService.tokenEndpoint,
    );

    if (this.oauthService.tokenEndpoint == null) {
      this.logger.log(
        this.constructor.name,
        'no tokenEndpoint available, load discovery document before continue',
      );
      await this.oauthService.loadDiscoveryDocument();
    }

    try {
      let resp = await this.oauthService.refreshToken();
      this.logger.log(
        this.constructor.name,
        '- Authentication token refreshed',
        resp,
      );

      this.setNextAuthenticationState(true);
      return true;
    } catch (error) {
      this.logger.warn(
        this.constructor.name,
        '- Could not refresh the authentication token',
        error,
        error?.message,
      );

      if (error instanceof HttpErrorResponse) {
        const httpError = error as HttpErrorResponse;
        if (
          httpError.status === 400 &&
          httpError.error?.error === 'invalid_grant'
        ) {
          this.logger.warn(
            this.constructor.name,
            'grant invalid, force a new login',
          );

          this.logoutWithoutAlert();
          return false;
        }
      }

      // Do not logout the user if the refresh failed but still has a valid access token
      if (!this.oauthService.hasValidAccessToken()) {
        this.logger.log(
          this.constructor.name,
          '- access token is already invalid - log out user',
        );
        this.logoutWithoutAlert();
        return false;
      }
      this.logger.log(
        this.constructor.name,
        '- user is still authenticated do not logout',
      );
      return true;
    }
  }

  // Private Functions
  private async setupOAuth(config: AuthConfig) {
    this.logger.log(this.constructor.name, '- Setup OAuth with', config);
    this.logger.log(
      this.constructor.name,
      '- Waiting for Storage to be loaded',
    );
    await this.mobileOauthStorage.isLoaded();

    this.logger.log(
      this.constructor.name,
      '- Storage loaded - Configure OAuthService',
    );
    if (!config.issuer) {
      let errorMessage = 'No issuer specified, failed oauth setup';
      this.logger.error(this.constructor.name, errorMessage);
      throw new Error(errorMessage);
    }

    this.logger.log(this.constructor.name, '- configure with config', config);
    this.oauthService.configure(config);
    this.logger.log(this.constructor.name, '- configured set jwsvalidator');
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();

    this.logger.log(
      this.constructor.name,
      '- check if valid access token is available',
    );
    if (this.oauthService.hasValidAccessToken()) {
      this.logger.log(this.constructor.name, '- Has valid accesstoken');
      this.setNextAuthenticationState(true);

      this.logger.log(
        this.constructor.name,
        '- load discovery document in the background user, do not block app usage as user has accesstoken',
      );
      this.loadDiscoverDocumentsIfAvailable();
    } else {
      this.logger.log(this.constructor.name, '- Has no valid accesstoken');
      this.logger.log(
        this.constructor.name,
        '- load discovery document before refreshing accesstoken',
      );
      await this.loadDiscoverDocumentsIfAvailable();

      try {
        this.logger.log(this.constructor.name, '- Refresh Access token');
        let accessToken = await this.oauthService.refreshToken();

        this.logger.log(
          this.constructor.name,
          '- Access token refreshed',
          accessToken,
        );
        this.setNextAuthenticationState(true);
      } catch (error) {
        this.logger.log(
          this.constructor.name,
          '- Could not refresh access token',
          error?.message,
        );
        this.setNextAuthenticationState(false);
      }
    }
    this.oauthService.events.subscribe(async (e) => {
      this.logger.log(this.constructor.name, '- session event received', e);
      if (e.type === 'token_expires') {
        this.logger.log(
          this.constructor.name,
          '- Authentication token expires soon - try refreshing',
        );
        await this.refreshToken();
      }
      if (e.type === 'discovery_document_load_error') {
        this.logger.warn(
          this.constructor.name,
          'could not connect to keycloak, continue in offline mode (3)',
        );
      }
    });
  }

  // JB20190405: Prevent notifying subscriber if there is no actual change
  private setNextAuthenticationState(nextState: boolean) {
    if (nextState !== this.authenticationState.value) {
      this.authenticationState.next(nextState);
    }
  }

  private async loadDiscoverDocumentsIfAvailable() {
    this.logger.log(this.constructor.name, '- try loading discovery document');
    try {
      if (this.oauthService.discoveryDocumentLoaded === false) {
        await this.oauthService.loadDiscoveryDocument();
      } else {
        this.logger.log(
          this.constructor.name,
          '- discovery document was already loaded',
        );
      }
    } catch (error) {
      this.logger.warn(
        this.constructor.name,
        'could not connect to keycloak, continue in offline mode (2)',
      );
    }
  }
}
